import Api from "./api";
import axios from "axios";

export default class ApiRequests {
  /**
   * Login a user with its credentials
   * @static
   * @param {Object} data Object containing login data
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static loginUser (data, onSuccess, onError) {
    Api.request("post", "/users/login", data, onSuccess, onError);
  }

  /**
   * Logout a user
   * @static
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static logoutUser (onSuccess, onError) {
    Api.request("get", "/users/logout", undefined, onSuccess, onError);
  }

  /**
   * Check the existence of the given username or email
   * @static
   * @param {Object} data Object containing user validation data
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static userNotExists (data, onSuccess, onError) {
    Api.request("post", "/users/not-exists", data, onSuccess, onError);
  }

  /**
   * Request a new password
   * @static
   * @param {Object} data Object containing new password (user validation) data
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static forgottenPassword (data, onSuccess, onError) {
    Api.request("post", "/users/forgotten-password", data, onSuccess, onError);
  }

  /**
   * Get current user profile info
   * @static
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static currentUser (onSuccess, onError) {
    Api.request("get", "/users/current", undefined, onSuccess, onError);
  }

  /**
   * Modify the current password
   * @static
   * @param {Object} data Object containing change password data
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static changePassword (data, onSuccess, onError) {
    Api.request("post", "/users/current/change-password", data, onSuccess, onError);
  }

  /**
   * Modify current user info
   * @static
   * @param {Object} data Object containing partial user data
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static modifyUser (data, onSuccess, onError) {
    Api.request("patch", "/users/current", data, onSuccess, onError);
  }

  /**
   * Create a new user
   * @static
   * @param {Object} data Object containing user data
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static createUser (data, onSuccess, onError) {
    Api.request("post", "/users", data, onSuccess, onError);
  }

  /**
   * Create a new admin user
   * @static
   * @param {Object} data Object containing admin data
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static createAdmin (data, onSuccess, onError) {
    Api.request("post", "/users/admin", data, onSuccess, onError);
  }

  /**
   * Show the list of all users (admin only)
   * @static
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static listUsers (onSuccess, onError) {
    Api.request("get", "/users", undefined, onSuccess, onError);
  }

  /**
   * Show the list of all enabled users
   * @static
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static listEnabledUsers (onSuccess, onError) {
    Api.request("get", "/users/enabled", undefined, onSuccess, onError);
  }

  /**
   * Show the list of all enabled users
   * @static
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static async listEnabledUsersPromise() {
    return await axios({
      method: "get",
      url: "/users/enabled"
    });
  }

  /**
   * Get list names of all users
   * @returns array ["name surname"]
   */
  static async listNames() {
    return await axios({
      method: "get",
      url: "/users/list/names"
    });
  }

  /**
   * Get list of cities of all users
   * @returns array ["Cities"]
   */
  static async listCities() {
    return await axios({
      method: "get",
      url: "/users/list/cities"
    });
  }

  /**
   * Get list of expertise area of all users
   * @returns array ["expertise area"]
   */
  static async listExpertise() {
    return await axios({
      method: "get",
      url: "/users/list/expertise"
    });
  }

  /**
   * Show given user info (admin only)
   * @static
   * @param {Number} id The id of a user
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static userInfo (id, onSuccess, onError) {
    Api.request("get", `/users/${id}`, undefined, onSuccess, onError);
  }

  /**
   * Return given user image
   * @static
   * @param {Number} id The id of a user
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static userImage (id, onSuccess, onError) {
    Api.request("get", `/users/${id}/image`, undefined, onSuccess, onError);
  }

  /**
   * Show given enabled user info
   * @static
   * @param {Number} id The id of a user
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static enabledUserInfo (id, onSuccess, onError) {
    Api.request("get", `/users/enabled/${id}`, undefined, onSuccess, onError);
  }

  /**
   * Modify an existing user (admin only)
   * @static
   * @param {Number} id The id of a user
   * @param {Object} data Object containing user data
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static modifyUserById (id, data, onSuccess, onError) {
    Api.request("patch", `/users/${id}`, data, onSuccess, onError);
  }

  /**
   * Delete an existing user (admin only)
   * @static
   * @param {Number} id The id of a user
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static deleteUser (id, onSuccess, onError) {
    Api.request("delete", `/users/${id}`, undefined, onSuccess, onError);
  }

  /**
   * Approve or enable an existing user (admin only)
   * @static
   * @param {Number} id The id of a user
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static enableUser (id, onSuccess, onError) {
    Api.request("patch", `/users/${id}/enable`, undefined, onSuccess, onError);
  }

  /**
   * Disable an existing user (admin only)
   * @static
   * @param {Number} id The id of a user
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static disableUser (id, onSuccess, onError) {
    Api.request("patch", `/users/${id}/disable`, undefined, onSuccess, onError);
  }

  /**
   * Return a list of available templates
   * @static
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static templateList (onSuccess, onError) {
    Api.request("get", `/templates/`, undefined, onSuccess, onError);
  }

  /**
   * Save email template data
   * @static
   * @param {Object} template data to be saved
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static saveTemplate (template, onSuccess, onError) {
    Api.request("patch", `/templates/${template.name}`, template, onSuccess, onError);
  }

  /**
   * Get sub-folders in a folder
   * @static
   * 
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static foldersList (onSuccess, onError) {
    Api.request("get", "/shared/folders/all", null, onSuccess, onError);
  }

  /**
   * Get documents in a folder
   * @static
   * 
   * @param {String} folder Requested folder
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static filesList (folder, onSuccess, onError) {
    Api.request("get", `/shared/${folder != undefined ? folder : ""}`, null, onSuccess, onError);
  }

  /**
   * Get document content
   * @static
   * 
   * @param {String} id ID of the requested file
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static fileContent (id, onSuccess, onError) {
    Api.request("download", `/shared/${id}/data`, null, onSuccess, onError);
  }

  /**
   * Update a document
   * @static
   * 
   * @param {String} id ID of the file to edit
   * @param {String} name New file name
   * @param {String} folder New folder
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static updateFile (id, name, folder, onSuccess, onError) {
    Api.request("patch", `/shared/${id}`, { name, folder }, onSuccess, onError);
  }

  /**
   * Delete a document
   * @static
   * 
   * @param {String} id ID of the file to delete
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static deleteFile (id, onSuccess, onError) {
    Api.request("delete", `/shared/${id}`, null, onSuccess, onError);
  }

  /**
   * Get month events
   *
   * @param {string} year Desired year
   * @param {string} month Desired month number
   * @param {Object => Void} onSuccess onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError onError Fallback function when the request fails
   */
  static getEvents (year, month, onSuccess, onError) {
    Api.request("get", `/calendar?year=${year}${month !== undefined ? "&month=" + month : ""}`, null, onSuccess, onError);
  }

  /**
   * Create a new event
   *
   * @param {Object} data Event data
   * @param {Object => Void} onError onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onSuccess onError Fallback function when the request fails
   */
  static createEvent (data, onSuccess, onError) {
    Api.request("post", `/calendar`, data, onSuccess, onError);
  }

  /**
   * Update an event
   *
   * @param {string} id Event ID
   * @param {Object} data Event data
   * @param {Object => Void} onError onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onSuccess onError Fallback function when the request fails
   */
  static updateEvent (id, data, onSuccess, onError) {
    Api.request("patch", `/calendar/${id}`, data, onSuccess, onError);
  }

  /**
   * Delete an event
   *
   * @param {string} id Event ID
   * @param {Object => Void} onError onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onSuccess onError Fallback function when the request fails
   */
  static deleteEvent (id, onSuccess, onError) {
    Api.request("delete", `/calendar/${id}`, null, onSuccess, onError);
  }

  /**
   * Upload a documents
   * @static
   *
   * @param {String} folder The folder name
   * @param {File[]} files List of documents to upload
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static uploadDocuments (folder, files, onSuccess, onError) {
    const formData = new FormData();

    // Add files to the request data
    files.forEach(file => formData.append("files", file));

    axios({
      method: "post",
      url: `/shared${folder != undefined ? "/" + folder : ""}/upload`,
      withCredentials: true,
      headers: { "Content-Type": "multipart/form-data" },
      data: formData,
    })
      .then(onSuccess)
      .catch(onError);
  }

  /**
   * Get the list of countries flags
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static getFlags(onSuccess, onError) {
    axios({
      method: "get",
      url: "img/flags/countries.json",
      baseURL: "" // static file, not a backend route
    })
      .then(onSuccess)
      .catch(onError);
  }

  /**
   * Get the list of available actions
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static getActions(onSuccess, onError) {
    Api.request("get", "/actions", null, onSuccess, onError);
  }

  /**
   * Register a new action for user
   * @param {Object} data Action data
   * @param {Object => Void} onError onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onSuccess onError Fallback function when the request fails
   */
  static sendAction(data, onSuccess, onError) {
    Api.request("post", "/actions/registered", data, onSuccess, onError);
  }

  /**
   * Get the list of registered by user actions
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static getActionsRegistered(onSuccess, onError) {
    Api.request("get", "/actions/registered", null, onSuccess, onError);
  }

  /**
   * Delete registered action
   * @param {string} id Action ID
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static deleteActionRegistered(id, onSuccess, onError) {
    Api.request("delete", `/actions/registered/${id}`, null, onSuccess, onError);
  }

  /**
   * Consume user points from accumulated actions
   * @param {*} points 
   * @param {*} onSuccess 
   * @param {*} onError 
   */
  static consumeUserPoints(points, onSuccess, onError) {
    Api.request("patch", `/actions/spend/${points}`, onSuccess, onError);
  }

  /**
   * Consume user points from accumulated actions
   * @param {*} points 
   * @param {*} userId 
   * @param {*} onSuccess 
   * @param {*} onError 
   */
  static consumeUserPointsAsAdmin(points, userId, onSuccess, onError) {
    Api.request("patch", `/actions/user-spend/${userId}/${points}`, onSuccess, onError);
  }

  /**
   * Return list of registered action reference (coordinates and userIDs)
   * @param {*} number request the last n references 
   * @param {*} limit timestamp (ms) to limit the available reference from
   * @param {*} onSuccess 
   * @param {*} onError 
   */
  static async getActionsReference(number, limit) {
    return await axios({
      method: "get",
      url: "/actions/registered/references"
    }, { params: { number, limit } });
  }

  /**
   * Send a new survey
   *
   * @param {Object} data Survey data
   * @param {Object => Void} onError onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onSuccess onError Fallback function when the request fails
   */
  static submitSurvey (data, onSuccess, onError) {
    Api.request("post", "/survey", data, onSuccess, onError);
  }

  /**
   * Get last payment info
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static getLastPayment(id, onSuccess, onError) {
    Api.request("get", `/payment/${id}/last`, null, onSuccess, onError);
  }

  /**
   * Get last survey info
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static getLastSurvey(id, onSuccess, onError) {
    Api.request("get", `/survey/${id}/last`, null, onSuccess, onError);
  }

  /**
   * Get expiration
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static getExpiration(id, onSuccess, onError) {
    Api.request("get", `payment/expirations/${id}`, null, onSuccess, onError);
  }

  /**
   * Get roles
   * @param {Object => Void} onSuccess Fallback function when the request succeeds
   * @param {Object => Void} onError Fallback function when the request fails
   */
  static getRoles(onSuccess, onError) {
    Api.request("get", "/roles?type=all", null, onSuccess, onError);
  }
}
